import React, { useEffect, useState } from 'react';
import { X, Search, Bookmark, BookmarkPlus, CheckCircle, Heart, Star, Filter, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
interface Angel {
  id: string;
  name: string;
  avatar: string;
  description: string;
  verified?: boolean;
  category?: string;
}
interface BookmarkGenerationFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onBookmarkAdded: (angelId: string, angelData: any) => void;
  ritualId?: string;
  ritualTarget?: number;
}
export const BookmarkGenerationFlow: React.FC<BookmarkGenerationFlowProps> = ({
  isOpen,
  onClose,
  onBookmarkAdded,
  ritualId,
  ritualTarget = 1
}) => {
  const [angels, setAngels] = useState<Angel[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [bookmarkedAngels, setBookmarkedAngels] = useState<string[]>([]);
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState<string[]>(['Popular', 'New', 'Verified', 'Suggested', 'Premium']);
  // Load sample angel data
  useEffect(() => {
    const sampleAngels: Angel[] = [{
      id: '1',
      name: 'Sophia',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/s1.png',
      description: 'Your personal fitness coach with a wild side',
      verified: true,
      category: 'Popular'
    }, {
      id: '2',
      name: 'Bonny Blue',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b1.png',
      description: 'Blonde bombshell ready to fulfill your fantasies',
      verified: true,
      category: 'Popular'
    }, {
      id: '3',
      name: 'Kim Holland',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fn5T3nQXxfgELfGiLrwqwP/k1.png',
      description: 'Mature beauty with years of experience',
      verified: true,
      category: 'Premium'
    }, {
      id: '4',
      name: 'Bella Blue',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5Qzoa8joo8CQwNu9hB7M4f/b3.png',
      description: 'Sweet and sensual, always ready to play',
      category: 'New'
    }, {
      id: '5',
      name: 'Harper',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
      description: 'The girl next door with a naughty secret',
      verified: true,
      category: 'Suggested'
    }, {
      id: '6',
      name: 'Zoey',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/fnQWPJWMus62iBnJCYdU4W/z1.png',
      description: 'Exotic beauty with a passion for adventure',
      category: 'New'
    }, {
      id: '7',
      name: 'Mia Khalifa',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg',
      description: 'Famous star now exclusively on our platform',
      verified: true,
      category: 'Premium'
    }, {
      id: '11',
      name: 'Lana',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
      description: 'Sensual and mysterious, ready to connect',
      verified: true,
      category: 'Suggested'
    }];
    setAngels(sampleAngels);
  }, []);
  // Filter angels based on search query and category
  const filteredAngels = angels.filter(angel => {
    const matchesSearch = angel.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory ? angel.category === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });
  // Handle bookmark toggling
  const toggleBookmark = (angelId: string) => {
    if (bookmarkedAngels.includes(angelId)) {
      // Remove bookmark
      setBookmarkedAngels(bookmarkedAngels.filter(id => id !== angelId));
    } else {
      // Add bookmark
      setBookmarkedAngels([...bookmarkedAngels, angelId]);
      // Find the angel data to pass to the parent component
      const angel = angels.find(a => a.id === angelId);
      if (angel) {
        onBookmarkAdded(angelId, {
          id: angelId,
          type: 'angel',
          title: angel.name,
          creator: {
            name: angel.name,
            avatar: angel.avatar,
            verified: angel.verified
          },
          thumbnail: angel.avatar,
          timestamp: 'Just now',
          isFavorite: false
        });
      }
      // Show success toast
      toast.success(`Bookmarked ${angels.find(a => a.id === angelId)?.name}`, {
        description: 'Added to your bookmarks collection'
      });
      // Dispatch ritual progress event
      const event = new CustomEvent('ritualProgress', {
        detail: {
          ritualId,
          progress: bookmarkedAngels.length + 1,
          target: ritualTarget
        }
      });
      document.dispatchEvent(event);
      // If we've reached the target, close the modal after a short delay
      if (bookmarkedAngels.length + 1 >= ritualTarget) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-2394">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-300 flex flex-col" data-id="element-2395">
        <div className="sticky top-0 z-10 p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between" data-id="element-2396">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2397">
            <BookmarkPlus size={20} className="text-pink-500" data-id="element-2398" />
            Bookmark Angels
            {ritualTarget > 1 && <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400" data-id="element-2399">
                ({bookmarkedAngels.length}/{ritualTarget})
              </span>}
          </h3>
          <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" onClick={onClose} aria-label="Close" data-id="element-2400">
            <X size={18} data-id="element-2401" />
          </button>
        </div>
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900" data-id="element-2402">
          <div className="flex flex-col md:flex-row gap-3" data-id="element-2403">
            <div className="relative flex-1" data-id="element-2404">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2405">
                <Search size={16} className="text-gray-400" data-id="element-2406" />
              </div>
              <input type="text" placeholder="Search angels..." className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-pink-600" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2407" />
            </div>
            <div className="relative" data-id="element-2408">
              <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => setShowCategories(!showCategories)} type="button" data-id="element-2409">
                <Filter size={16} className="text-gray-500 dark:text-gray-400" data-id="element-2410" />
                <span className="text-gray-700 dark:text-gray-300" data-id="element-2411">
                  {selectedCategory || 'All Categories'}
                </span>
                <ChevronDown size={14} className="text-gray-500 dark:text-gray-400" data-id="element-2412" />
              </button>
              {showCategories && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10" data-id="element-2413">
                  <div className="p-2" data-id="element-2414">
                    <button className={`w-full text-left px-3 py-2 text-sm rounded-md ${!selectedCategory ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} onClick={() => {
                  setSelectedCategory(null);
                  setShowCategories(false);
                }} type="button" data-id="element-2415">
                      All Categories
                    </button>
                    {categories.map(category => <button key={category} className={`w-full text-left px-3 py-2 text-sm rounded-md ${selectedCategory === category ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`} onClick={() => {
                  setSelectedCategory(category);
                  setShowCategories(false);
                }} type="button" data-id="element-2416">
                        {category}
                      </button>)}
                  </div>
                </div>}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-2417">
          {filteredAngels.length > 0 ? filteredAngels.map(angel => <div key={angel.id} className={`relative bg-white dark:bg-gray-800 rounded-lg border ${bookmarkedAngels.includes(angel.id) ? 'border-pink-500 dark:border-pink-600 shadow-md shadow-pink-500/10' : 'border-gray-200 dark:border-gray-700 hover:border-pink-300 dark:hover:border-pink-800'} overflow-hidden transition-all duration-200`} data-id="element-2418">
                <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden" data-id="element-2419">
                  <img src={angel.avatar || 'https://uploadthingy.s3.us-west-1.amazonaws.com/5EGQyKTFiaCuCDqrstpdLF/image.png'} alt={angel.name} className="w-full h-full object-cover" onError={e => {
              // Fallback to placeholder on error
              e.currentTarget.src = 'https://uploadthingy.s3.us-west-1.amazonaws.com/5EGQyKTFiaCuCDqrstpdLF/image.png';
            }} data-id="element-2420" />
                  {angel.verified && <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-2421">
                      <CheckCircle size={10} data-id="element-2422" />
                      <span data-id="element-2423">Verified</span>
                    </div>}
                  {angel.category === 'Premium' && <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-2424">
                      PREMIUM
                    </div>}
                  {angel.category === 'New' && <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-2425">
                      NEW
                    </div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end" data-id="element-2426">
                    <div className="p-3 w-full" data-id="element-2427">
                      <div className="flex justify-between items-center" data-id="element-2428">
                        <div data-id="element-2429">
                          <h4 className="text-white font-medium" data-id="element-2430">
                            {angel.name}
                          </h4>
                        </div>
                        <div className="flex gap-1" data-id="element-2431">
                          <button className="p-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors" type="button" onClick={e => {
                      e.stopPropagation();
                      // Heart action (like functionality)
                      toast.success(`Liked ${angel.name}!`);
                    }} data-id="element-2432">
                            <Heart size={16} data-id="element-2433" />
                          </button>
                          <button className="p-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors" type="button" onClick={e => {
                      e.stopPropagation();
                      // Star action (favorite functionality)
                      toast.success(`Added ${angel.name} to favorites!`);
                    }} data-id="element-2434">
                            <Star size={16} data-id="element-2435" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-3" data-id="element-2436">
                  <div className="flex items-center justify-between" data-id="element-2437">
                    <h4 className="font-medium text-gray-900 dark:text-white" data-id="element-2438">
                      {angel.name}
                    </h4>
                    <button className={`p-1.5 rounded-full ${bookmarkedAngels.includes(angel.id) ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-500' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-pink-100 dark:hover:bg-pink-900/30 hover:text-pink-500'} transition-colors`} onClick={e => {
                e.stopPropagation();
                toggleBookmark(angel.id);
              }} type="button" aria-label={bookmarkedAngels.includes(angel.id) ? 'Remove bookmark' : 'Add bookmark'} data-id="element-2439">
                      <Bookmark size={16} fill={bookmarkedAngels.includes(angel.id) ? 'currentColor' : 'none'} data-id="element-2440" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2" data-id="element-2441">
                    {angel.description || 'Angel description'}
                  </p>
                </div>
                {bookmarkedAngels.includes(angel.id) && <div className="absolute inset-0 bg-pink-500/10 dark:bg-pink-900/20 pointer-events-none" data-id="element-2442"></div>}
              </div>) : <div className="col-span-full flex flex-col items-center justify-center py-12" data-id="element-2443">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-2444">
                <Search size={24} className="text-gray-400" data-id="element-2445" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-2446">
                No angels found
              </h4>
              <p className="text-gray-500 dark:text-gray-400 text-center max-w-md" data-id="element-2447">
                Try adjusting your search or filter to find angels to bookmark.
              </p>
            </div>}
        </div>
        <div className="sticky bottom-0 p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex justify-between items-center" data-id="element-2448">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2449">
            {bookmarkedAngels.length}{' '}
            {bookmarkedAngels.length === 1 ? 'angel' : 'angels'} bookmarked
            {ritualTarget > 1 && ` (${ritualTarget - bookmarkedAngels.length} more to complete ritual)`}
          </div>
          <div className="flex gap-2" data-id="element-2450">
            <button className="px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" onClick={e => {
            e.preventDefault();
            onClose();
          }} type="button" data-id="element-2451">
              Cancel
            </button>
            <button className={`px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg transition-all flex items-center gap-1.5 ${bookmarkedAngels.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:shadow-pink-500/20'}`} onClick={e => {
            e.preventDefault();
            if (bookmarkedAngels.length > 0) {
              toast.success('Bookmarks saved successfully!');
              onClose();
            }
          }} disabled={bookmarkedAngels.length === 0} type="button" data-id="element-2452">
              <Bookmark size={16} data-id="element-2453" />
              <span data-id="element-2454">Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>;
};